<template>
  <v-fade-transition>
    <div
      v-show="!hidden"
      :class="{ backdrop: !hideBackdrop }"
    >
      <v-progress-circular
        :rotate="360"
        :model-value="showIndeterminate ? undefined : progress"
        :indeterminate="showIndeterminate"
        :size="size"
        :width="width"
        :color="color"
      >
        <slot :progress="progress">
          <h2 v-if="!hideValue">{{ progress.toFixed(0) }} %</h2>
        </slot>
      </v-progress-circular>
    </div>
  </v-fade-transition>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface Props {
  size?: number
  width?: number
  color?: string
  duration?: number
  completed?: boolean
  hideValue?: boolean
  hideBackdrop?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 100,
  width: 8,
  color: 'primary',
  duration: 20000,
  completed: false,
  hideValue: false,
  hideBackdrop: false,
})

const hidden = ref(false)

const progress = ref(0)
const hideTimeout = ref<number | null>(null)
const interval = ref<number | null>(null)
const startTime = ref<number | null>(null)
const showIndeterminate = computed(() => progress.value === 99 && !props.completed)

const startProgress = () => {
  if (!props.completed) {
    startTime.value = Date.now()
    interval.value = window.setInterval(updateProgress, 100)
  }
}

const updateProgress = () => {
  if (!startTime.value) return

  const elapsed = Date.now() - startTime.value
  const t = elapsed / props.duration

  if (t <= 1) {
    // Linear progress to 80% during initial duration
    progress.value = Math.min(80, t * 80)
  } else if (t <= 2) {
    // Additional 100% of duration for remaining 19%
    // Map t from 1-2 to 0-1 for the overtime period
    const overtimeProgress = t - 1
    // Add remaining progress with easing function (slower towards the end)
    const easedProgress = 1 - Math.pow(1 - overtimeProgress, 2) // Quadratic easing
    progress.value = Math.min(99, 80 + easedProgress * 19)
  } else {
    progress.value = 99 // Stay at 99% until completed
  }
}

const completeProgressDelayed = () => {
  if (interval.value) clearInterval(interval.value)
  progress.value = 100

  hideTimeout.value = window.setTimeout(() => {
    hidden.value = true
  }, 500)
}

watch(
  () => props.completed,
  (newVal) => {
    if (newVal) {
      completeProgressDelayed()
    } else {
      if (interval.value) {
        clearInterval(interval.value)
        interval.value = null
      }

      if (hideTimeout.value) {
        clearTimeout(hideTimeout.value)
      }

      hidden.value = false
      progress.value = 0
      startTime.value = Date.now()
      startProgress()
    }
  }
)

// Initialize hidden state based on completed prop
hidden.value = props.completed

onMounted(() => {
  if (!props.completed) {
    startProgress()
  }
})

onBeforeUnmount(() => {
  if (interval.value) {
    clearInterval(interval.value)
  }

  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value)
  }
})
</script>

<style scoped>
.backdrop {
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
